import { Tab } from '@savgroup-front-common/core/atoms/Tabs/Tabs.types';
import { ViewMode } from '@savgroup-front-common/core/atoms/ViewModeToggle';

import { AppIncident, AppScheduledMaintenance } from './AppStatuses';
import { BaseAutocompleteOption } from './Autocomplete';
import {
  ActiveFilterPreset,
  FilterCategory,
  FilterSASDto,
  FilterValue,
  SelectedFilters,
} from './FilterList';
import { KEYBOARD_LAYOUT } from './KeyboardLayout';
import { WelcomePageRealizedActions } from './Onboarding';
import { RESULT_TYPE } from './Search';

export enum LOCAL_STORAGE_KEYS {
  KEYBOARD_LAYOUT = 'keyboardLayout',
  DARK_MODE = 'darkMode',
  APP_SCHEDULED_MAINTENANCES = 'appScheduledMaintenances',
  APP_INCIDENTS = 'appIncidents',
  FILTERS_LIST_FILES = 'filtersListFiles',
  FILTERS_LIST_TODOS = 'filtersListTodos',
  FILTERS_LIST_ALERTS = 'filtersListAlerts',
  FILTERS_LIST_SAS_DTO = 'filtersListSASDto',
  FILTERS_LIST_PRESET = 'filtersListPreset',
  SEARCH_RESULT_TYPES = 'search-result-types',
  MASTER_DETAIL = 'master_detail',
  LOCALE = 'locale',
  IS_FILE_STATUS_ACTIONS_DISPLAYED = 'isFileStatusActionsDisplayed',
  FILTERS_FILE_HISTORY = 'filtersFileHistory',
  DEFAULT_SELLER_ID_CONFIGURATION_CONSOLE = 'defaultSellerIdConfigurationConsole',
  DEFAULT_VIEW_CONFIGURATION_CONSOLE = 'defaultViewConfigurationConsole',
  CONFIGURATION_CONSOLE_FILTERS = 'configurationConsoleFilters',
  ON_BOARDING_REALIZED_ACTIONS = 'onBoardingRealizedActions',
  NEW_BO_ENABLED = 'newBoEnabled_v3',
  NEW_BO_ENABLED_V4 = 'newBoEnabled_v4',
  PRICE_ORDER = 'PRICE_ORDER',
  AUTH_DUAL_LAYOUT = 'AUTH_DUAL_LAYOUT',
  FILES_LIST_FILTERS = 'newUiFilesListFilters',
  TODOS_LIST_FILTERS = 'newUiTodosListFilters',
  USERS_LIST_FILTERS = 'newUiUsersListFilters',
  SELECTED_SELLER = 'selectedSeller',
  FILES_TAB = 'fileTab',
  JOBS_TAB = 'jobsTab',
  SPARE_PARTS_TAB = 'sparePartsTab',
  TRANSPORT_TAB = 'transportTab',
  REQUESTS_TO_VALIDATE_FILTERS = 'requestsToValidateFilters',
  STOCK_ITEMS_FILTERS = 'stockItemsFilters',
  SUPPLIERS_REQUESTS_FILTERS = 'suppliersRequestsFilters',
  DELIVERY_REQUESTS_FILTERS = 'deliveryRequestsFilters',
  INTERVENTION_REPORT_TAB = 'interventionReportTab',
  INTERVENTION_REPORT_FILTERS = 'interventionReportFilters',
  ORDERS_TAB = 'ordersTab',
  BILLING_TAB = 'billingTab',
  HOME_INTERVENTION_TAB = 'homeInterventionTab',
  HOME_INTERVENTION_FILTERS = 'homeInterventionFilters',
  HOME_INTERVENTION_PLANNING_DATE = 'homeInterventionPlanningDate',
  ORDERS_LIST_FILTERS = 'ordersListFilters',
  FILE_HISTORY_FILTERS = 'fileHistoryFilters',
  DOCUMENT_LIST_VIEW_MODE = 'documentListViewMode',
  DYNAMIC_TERM_SUGGESTIONS = 'dynamicTermSuggestions',
  TRANSITION_SUGGESTIONS_MODE = 'transitionSuggestionsMode',
}

export const TRANSITION_SUGGESTIONS_MODE = {
  SMART: 'smart',
  CLASSIC: 'classic',
} as const;

export type TransitionSuggestionsMode =
  (typeof TRANSITION_SUGGESTIONS_MODE)[keyof typeof TRANSITION_SUGGESTIONS_MODE];

export const DYNAMIC_TERM_SUGGESTION_STATUS = {
  PENDING: 'pending',
  SENT: 'sent',
} as const;

export type DynamicTermSuggestionStatus =
  (typeof DYNAMIC_TERM_SUGGESTION_STATUS)[keyof typeof DYNAMIC_TERM_SUGGESTION_STATUS];

export interface DynamicTermSuggestionEntry {
  key: string;
  status: DynamicTermSuggestionStatus;
  updatedAt: number;
}

export interface LocalStorageValues {
  [LOCAL_STORAGE_KEYS.KEYBOARD_LAYOUT]: KEYBOARD_LAYOUT;
  [LOCAL_STORAGE_KEYS.DARK_MODE]: boolean;
  [LOCAL_STORAGE_KEYS.FILTERS_LIST_FILES]: FilterCategory[];
  [LOCAL_STORAGE_KEYS.FILTERS_LIST_TODOS]: FilterCategory[];
  [LOCAL_STORAGE_KEYS.FILTERS_LIST_ALERTS]: FilterCategory[];
  [LOCAL_STORAGE_KEYS.FILTERS_LIST_SAS_DTO]: FilterSASDto;
  [LOCAL_STORAGE_KEYS.FILTERS_LIST_PRESET]?: ActiveFilterPreset;
  [LOCAL_STORAGE_KEYS.SEARCH_RESULT_TYPES]: RESULT_TYPE[];
  [LOCAL_STORAGE_KEYS.LOCALE]: string;
  [LOCAL_STORAGE_KEYS.IS_FILE_STATUS_ACTIONS_DISPLAYED]: boolean;
  [LOCAL_STORAGE_KEYS.APP_SCHEDULED_MAINTENANCES]: AppScheduledMaintenance[];
  [LOCAL_STORAGE_KEYS.APP_INCIDENTS]: AppIncident[];
  [LOCAL_STORAGE_KEYS.MASTER_DETAIL]: Record<string, boolean>;
  [LOCAL_STORAGE_KEYS.DEFAULT_SELLER_ID_CONFIGURATION_CONSOLE]: string;
  [LOCAL_STORAGE_KEYS.DEFAULT_VIEW_CONFIGURATION_CONSOLE]: string;
  [LOCAL_STORAGE_KEYS.FILTERS_FILE_HISTORY]: {
    isOpen: boolean;
    defaultFilters?: Record<string, BaseAutocompleteOption[] | undefined>;
  };
  [LOCAL_STORAGE_KEYS.CONFIGURATION_CONSOLE_FILTERS]: Record<
    string,
    SelectedFilters[]
  >;
  [LOCAL_STORAGE_KEYS.ON_BOARDING_REALIZED_ACTIONS]: WelcomePageRealizedActions;
  [LOCAL_STORAGE_KEYS.FILES_TAB]: Tab[];
  [LOCAL_STORAGE_KEYS.SPARE_PARTS_TAB]: Tab[];
  [LOCAL_STORAGE_KEYS.TRANSPORT_TAB]: Tab[];
  [LOCAL_STORAGE_KEYS.ORDERS_TAB]: Tab[];
  [LOCAL_STORAGE_KEYS.HOME_INTERVENTION_FILTERS]: {
    technicianValues: FilterValue<{ type: any }>[];
    siteValues: FilterValue<{ type: any }>[];
    interventionTypeValues: FilterValue<{ type: any }>[];
    pieceStatusValues: FilterValue<{ type: any }>[];
  };
  [LOCAL_STORAGE_KEYS.HOME_INTERVENTION_PLANNING_DATE]: string;
  [LOCAL_STORAGE_KEYS.DOCUMENT_LIST_VIEW_MODE]: ViewMode;
  [LOCAL_STORAGE_KEYS.DYNAMIC_TERM_SUGGESTIONS]: Record<
    string,
    DynamicTermSuggestionEntry
  >;
  [LOCAL_STORAGE_KEYS.TRANSITION_SUGGESTIONS_MODE]: TransitionSuggestionsMode;
}
